// ============================================
// Mars Shaders
// Fragment and vertex shaders for Mars rendering
// ============================================

import { earthShaderFunctions } from './common';

/**
 * Mars surface vertex shader
 */
export const marsVertexShader = `
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vPosition;

    void main() {
        vUv = uv;
        vNormal = normalize(normalMatrix * normal);
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

/**
 * Mars surface fragment shader - red-orange surface with polar caps
 */
export const marsFragmentShader = `
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vPosition;
    uniform float time;
    uniform vec3 sunDirection;

    ${earthShaderFunctions}

    void main() {
        vec2 uv = vUv;
        uv.x = fract(uv.x + 0.25);

        // Fixed view direction
        vec3 viewDir = vec3(0.0, 0.0, 1.0);
        vec3 normal = normalize(vNormal);

        // Fresnel coefficient
        float fresnel = 1.0 - max(0.0, dot(normal, viewDir));
        float fresnelPow = pow(fresnel, 1.5);

        // Sun light calculation
        float sunLight = max(0.0, dot(normal, sunDirection));
        float ambient = 0.25;
        float diffuse = sunLight * 0.75;
        float lighting = ambient + diffuse;

        // ===== Mars base colors =====
        vec3 rustRed = vec3(0.75, 0.35, 0.22);      // Main rust color
        vec3 darkRed = vec3(0.50, 0.25, 0.18);      // Dark regions
        vec3 orangeRed = vec3(0.85, 0.45, 0.28);    // Bright regions
        vec3 dusty = vec3(0.82, 0.55, 0.40);        // Dusty areas
        vec3 polarIce = vec3(0.95, 0.97, 1.0);      // Polar ice caps

        // Surface variation
        float surfaceNoise = fbm(uv * 15.0, 5);
        float surfaceDetail = fbm(uv * 40.0, 4);

        // Create terrain variety
        vec3 baseColor = rustRed;
        baseColor = mix(baseColor, darkRed, smoothstep(0.4, 0.6, surfaceNoise) * 0.5);
        baseColor = mix(baseColor, orangeRed, smoothstep(0.55, 0.75, surfaceDetail) * 0.4);
        baseColor = mix(baseColor, dusty, smoothstep(0.6, 0.8, noise(uv * 25.0)) * 0.3);

        // ===== Major features =====
        // Olympus Mons (shield volcano)
        vec2 olympusPos = vec2(0.38, 0.55);
        float olympusDist = length((uv - olympusPos) / vec2(0.08, 0.07));
        float olympusFactor = smoothstep(1.0, 0.3, olympusDist);
        baseColor = mix(baseColor, orangeRed * 1.1, olympusFactor * 0.5);
        // Caldera
        float caldera = smoothstep(0.2, 0.15, olympusDist);
        baseColor = mix(baseColor, darkRed * 0.8, caldera * 0.6);

        // Valles Marineris (canyon system)
        float vmLat = 0.45;
        float vmFactor = smoothstep(0.02, 0.008, abs(uv.y - vmLat)) *
                        smoothstep(0.3, 0.45, uv.x) * smoothstep(0.7, 0.55, uv.x);
        baseColor = mix(baseColor, darkRed * 0.6, vmFactor * 0.7);

        // Hellas Basin (impact crater)
        vec2 hellasPos = vec2(0.72, 0.32);
        float hellasDist = length((uv - hellasPos) / vec2(0.1, 0.08));
        float hellasFactor = smoothstep(1.0, 0.4, hellasDist);
        baseColor = mix(baseColor, dusty, hellasFactor * 0.4);

        // ===== Polar ice caps =====
        float latitude = abs(uv.y - 0.5) * 2.0;

        // North polar cap
        float northPolar = smoothstep(0.75, 0.95, uv.y);
        northPolar *= (1.0 + sin(uv.x * 20.0) * 0.1);  // Irregular edge
        northPolar += noise(uv * 50.0) * 0.15 * smoothstep(0.7, 0.9, uv.y);

        // South polar cap (smaller)
        float southPolar = smoothstep(0.25, 0.08, uv.y);
        southPolar *= (1.0 + cos(uv.x * 18.0) * 0.08);
        southPolar += noise(uv * 45.0) * 0.1 * smoothstep(0.3, 0.1, uv.y);

        float polarFactor = clamp(northPolar + southPolar, 0.0, 1.0);
        baseColor = mix(baseColor, polarIce, polarFactor * 0.9);

        // ===== Dust storms (subtle) =====
        float stormNoise = fbm(vec2(uv.x * 8.0 + time * 0.02, uv.y * 6.0), 4);
        float stormFactor = smoothstep(0.55, 0.7, stormNoise) * 0.15;
        baseColor = mix(baseColor, dusty * 1.2, stormFactor);

        // ===== Apply lighting =====
        vec3 surfaceColor = baseColor * lighting;

        // Subtle atmospheric haze (thin Martian atmosphere)
        vec3 atmosphereColor = vec3(0.85, 0.65, 0.55);
        float rimLight = pow(fresnel, 2.2) * 0.35;
        surfaceColor = mix(surfaceColor, atmosphereColor, rimLight);

        // Night side darkening
        float nightSide = 1.0 - smoothstep(0.0, 0.15, sunLight);
        surfaceColor *= mix(1.0, 0.2, nightSide);

        // Terminator glow (dusty atmosphere)
        float terminator = smoothstep(0.0, 0.2, sunLight) * smoothstep(0.3, 0.15, sunLight);
        surfaceColor += atmosphereColor * terminator * fresnel * 0.15;

        gl_FragColor = vec4(surfaceColor, 1.0);
    }
`;

/**
 * Mars moon (Phobos/Deimos) vertex shader
 */
export const marsMoonVertexShader = `
    varying vec2 vUv;
    varying vec3 vNormal;

    void main() {
        vUv = uv;
        vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

/**
 * Mars moon fragment shader - irregular gray rocky surface
 */
export const marsMoonFragmentShader = `
    varying vec2 vUv;
    varying vec3 vNormal;
    uniform vec3 sunDirection;

    ${earthShaderFunctions}

    void main() {
        vec3 viewDir = vec3(0.0, 0.0, 1.0);
        vec3 normal = normalize(vNormal);

        // Lighting
        float sunLight = max(0.0, dot(normal, sunDirection));
        float ambient = 0.2;
        float diffuse = sunLight * 0.8;
        float lighting = ambient + diffuse;

        // Dark gray rocky surface
        vec3 baseColor = vec3(0.35, 0.32, 0.30);
        float variation = noise(vUv * 20.0) * 0.15;
        baseColor += variation;

        // Craters
        float craterNoise = fbm(vUv * 15.0, 4);
        baseColor -= craterNoise * 0.1;

        vec3 surfaceColor = baseColor * lighting;

        gl_FragColor = vec4(surfaceColor, 1.0);
    }
`;
